import {
    ConditionalRender,
    isFailed,
    isFailedByOffline,
    isShowLoader,
    useAppSelector,
    useDesignGeneration,
    useNavigationBlockerViaHistory,
    useOnline,
    useSystemAction,
    useTranslation,
    useTriggerAction,
} from '@libs/core';
import { ActionSystemActionType, AssetsSvgMap, TCommonData, TWidget } from '@libs/types';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { CommonScreen } from './common/CommonScreen/CommonScreen';
import { getRebranding } from './hooks/useRebranding';
import { useSlowRequestGuard } from './hooks/useSlowRequestGuard';
import { getSemanticColors, SemanticColor } from './theme/colors';
import { EWidgetVersion, TranslationKeys } from './types/enums';
import styled from './widgetWrapper.module.css';

const LOADER_DELAY = 800;
const EMPTY_WIDGET = {} as TWidget<TCommonData>;

export const WidgetWrapper = ({ children }: { children: React.ReactNode }) => {
    const isLoader = useAppSelector(isShowLoader);
    const isOnline = useOnline();
    const isStatusFlailed = useAppSelector(isFailed);
    const isOfflineFailure = useAppSelector(isFailedByOffline);
    const navigate = useNavigate();
    const designGeneration = useDesignGeneration();
    // App-level screens have no owning widget, so they follow the backend design generation.
    const version = getRebranding() ? (EWidgetVersion.V2 as number) : designGeneration;

    const { triggerAction } = useTriggerAction(EMPTY_WIDGET, true);
    const { t } = useTranslation();
    const systemActionNavigateBack = useSystemAction(ActionSystemActionType.navigateBack);
    useNavigationBlockerViaHistory(true, { triggerAction, systemActionNavigateBack });

    // Toast, then offline / slow-service popup for a request that keeps hanging on the wire.
    useSlowRequestGuard({ isOnline, triggerAction });

    useEffect(() => {
        if (isLoader) {
            // @ts-expect-error
            document?.activeElement?.blur?.();
        }
    }, [isLoader]);

    return (
        <>
            {/* A failure caused by a lost connection is handled by the offline flow (toast + popup), so the generic error screen stays hidden even once the network is back. */}
            <ConditionalRender condition={isOnline && isStatusFlailed && !isOfflineFailure}>
                <CommonScreen
                    version={version}
                    useIcon={false}
                    fill="pink_100"
                    iconName={AssetsSvgMap.ic_x_circle_thin_80}
                    mainText={t(TranslationKeys.GeneralErrorText, 'Something went wrong...')}
                    buttonLabel={t(TranslationKeys.ButtonRetry, 'Try again')}
                    buttonHandleClick={() => {
                        navigate('/clearSession');
                    }}
                />
            </ConditionalRender>
            <ConditionalRender condition={isLoader}>
                <div className={styled.overlayContainer}>
                    <ConditionalRender condition={isLoader} delay={LOADER_DELAY}>
                        <CommonScreen
                            version={version}
                            useBackground={false}
                            useSpinner
                            animationsColor={getSemanticColors(version)[SemanticColor.TextEmphasised]}
                        />
                    </ConditionalRender>
                </div>
            </ConditionalRender>
            {children}
        </>
    );
};
